/**
 * Per-content page presentation, stored as a JSON string in contents.page_style
 * (authored in Diapason's content editor). Parsing is tolerant like parseThemeTokens:
 * anything malformed degrades to null (= app default), never throws.
 *
 * Background modes:
 *  - default:      the theme's page background (what every page shows today)
 *  - surface:      the theme's surface tone (soft card tint over the whole page)
 *  - primary-soft: a translucent wash of the chrome color — stays on-brand and
 *                  updates itself when the study theme changes
 *  - custom:       an explicit hex the researcher picked (frozen; contrast-checked
 *                  at authoring time in Diapason)
 */

/**
 * App pages' background: the header color fading into the theme background
 * (derived in tokens.ts). Pages with the tinted page also get the decoration that
 * comes with the header edge (ResonanceLayout keys that off this exact value).
 * The fallback covers the first paint, before applyThemeTokens has run.
 */
export const PAGE_BACKGROUND = "var(--app-page-background, var(--app-color-background, #ffffff))";

/** The page tint without the decoration, for pages with their own visuals
 *  (Mental+'s island pages, whose slider arrows sit where the bubbles would). */
export const PLAIN_PAGE_BACKGROUND =
  "var(--app-page-background-plain, var(--app-color-background, #ffffff))";

/** Question screens and reading pages (articles): the same tint, shorter, and
 *  never any decoration — nothing distracts while answering or reading. */
export const QUESTION_BACKGROUND =
  "var(--app-question-background, var(--app-color-background, #ffffff))";

/** The tint's top color, for anything that must paint opaquely on it (the space
 *  behind the header's wavy edge, a fixed bar under the header). */
export const PAGE_TOP_FILL = "var(--app-page-top, var(--app-color-background, #ffffff))";

const THEME_BACKGROUND = "var(--app-color-background, #ffffff)";
/** Bottom of the header band — same formula as the WaveToolbar height. */
const HEADER_BOTTOM = "calc(38vh * (163 / 500))";

/**
 * Tinted page for screens with a FIXED bar right under the header (Mental+'s
 * Vidéos search bar, Ma bouée's tabs). The tint stays flat under the bar too, so
 * the bar — painted PAGE_TOP_FILL to hide what scrolls beneath it — blends in with
 * no seam; then it fades like PAGE_BACKGROUND. No decoration (the bar covers that
 * band). Pages using it also pass PAGE_TOP_FILL as their headerBackground.
 */
export const pageBackgroundUnderBar = (barHeightPx: number): string =>
  `linear-gradient(180deg, ${PAGE_TOP_FILL} 0px, ${PAGE_TOP_FILL} calc(${HEADER_BOTTOM} + ${barHeightPx}px), ` +
  `var(--app-page-mid, ${THEME_BACKGROUND}) calc(${HEADER_BOTTOM} + ${barHeightPx + 196}px), ` +
  `${THEME_BACKGROUND} calc(${HEADER_BOTTOM} + ${barHeightPx + 496}px)), ${THEME_BACKGROUND}`;

export type PageBackgroundMode = "default" | "surface" | "primary-soft" | "gradient" | "custom";

export interface PageStyle {
  background: { mode: PageBackgroundMode; hex?: string };
}

const MODES: PageBackgroundMode[] = ["default", "surface", "primary-soft", "gradient", "custom"];
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

export const parsePageStyle = (value?: string | null): PageStyle | null => {
  if (typeof value !== "string" || value.trim() === "") return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch (e) {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;

  const background = (parsed as { background?: unknown }).background;
  if (typeof background !== "object" || background === null) return null;

  const { mode, hex } = background as { mode?: unknown; hex?: unknown };
  if (typeof mode !== "string" || !MODES.includes(mode as PageBackgroundMode)) return null;
  if (mode === "custom" && (typeof hex !== "string" || !HEX_COLOR_RE.test(hex.trim()))) return null;

  return {
    background: {
      mode: mode as PageBackgroundMode,
      ...(mode === "custom" ? { hex: (hex as string).trim() } : {}),
    },
  };
};

/**
 * CSS background value for a page — theme-derived modes are pure CSS-var expressions.
 *
 * Every mode resolves to an OPAQUE composite: translucent washes are layered over
 * their own theme-background base (`background: <wash-layer>, <base-color>`). A bare
 * rgba() value looked right on a settled page (the white app root sat behind it) but
 * let the LEAVING page show through during Ionic route transitions.
 */
export const resolvePageBackground = (style: PageStyle | null): string => {
  const base = "var(--app-color-background, white)";
  switch (style?.background.mode) {
    case "surface":
      return "var(--app-color-surface, #ffffff)";
    case "primary-soft":
      return `linear-gradient(rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.16), rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.16)), ${base}`;
    case "gradient":
      // Soft chrome-colored wash fading into the theme background — follows the
      // study theme wherever it goes. No `fixed` attachment: it paints on the
      // non-scrolling page frame (IonContent scrolls inside it), and fixed
      // attachment is an iOS-Safari repaint hazard.
      return `linear-gradient(180deg, rgba(var(--app-color-primary-rgb, 171, 240, 239), 0.30) 0%, var(--app-color-background, white) 55%), ${base}`;
    case "custom":
      return style.background.hex ?? base;
    default:
      return base;
  }
};

/** One-step convenience for content consumers. */
export const pageBackgroundOf = (pageStyleJson?: string | null): string =>
  resolvePageBackground(parsePageStyle(pageStyleJson));

/**
 * In-app reading pages (resource articles): an authored page background wins;
 * otherwise ("default" mode or no style) the app's reading tint instead of the
 * plain theme background. Pre-login content keeps pageBackgroundOf.
 */
export const readingPageBackgroundOf = (pageStyleJson?: string | null): string => {
  const style = parsePageStyle(pageStyleJson);
  if (!style || style.background.mode === "default") return QUESTION_BACKGROUND;
  return resolvePageBackground(style);
};
